import { Injectable } from '@angular/core';
import { cloneDeep } from 'src/ep-utils';

export enum EditStatusType {
    Add = 'A',
    Change = 'C',
    Delete = 'D',
    Available = '?',
    PreviousDelete = 'X',
    Reactivated = 'R',
    NoChange = 'N'
}

@Injectable()
export class StateHelperService {
    newID: number = 0;

    getTempID() {
        this.newID--;
        if (this.newID < -999999) {
            this.newID = -1;
        }
        return this.newID.toString();
    }

    getNextTempID(items: any, idFieldName: string): string {
        let newTempID = 0;
        let itemArray: any = [];
        for (var i in items) {
            itemArray.push(items[i]);
        }

        itemArray.sort((a, b) => a[idFieldName] - b[idFieldName]);

        for (var i in itemArray) {
            newTempID = parseInt(itemArray[i][idFieldName], 10);
            break;
        }

        newTempID--;

        if (newTempID > -1) {
            newTempID = -1;
        }

        return newTempID.toString();
    }

    //
    // THIS USED BY UX DESIGNER DYNAMIC RULES, please use the normal
    // TempID generator, getNextTempID() function.
    //
    getNextID(items: any, idFieldName: string): string {
        let newTempID = 0;
        let itemArray: any = [];
        for (var i in items) {
            itemArray.push(items[i]);
        }

        itemArray.sort((a, b) => b[idFieldName] - a[idFieldName]);

        for (var i in itemArray) {
            newTempID = parseInt(itemArray[i][idFieldName], 10);
            break;
        }

        newTempID++;

        if (!newTempID || newTempID < 0) {
            newTempID = 1;
        }

        return newTempID.toString();
    }

    getNextItemNumber(items: any, property: string, filterByNodeType: string = null) {
        // return the next highest number from a group of entities based on a property.   
        // Example is to get the next location number or get the next book_Veh_Num
        let newItemNumber = 1;
        for (var i in items) {
            //some item arrays (like articles) contain multiple nodeTypes -- use the filterByNodeType parameter to limit eval to a single nodetype
            if (filterByNodeType && filterByNodeType > '' && filterByNodeType != items[i].nodeType) {
                continue;
            }
            if (items[i][property] * 1 >= newItemNumber) {
                newItemNumber = items[i][property] * 1 + 1;
            }
        }
        return newItemNumber;
    }

    // sets edit status to add
    setItemAdded(item: any) {
        item.Edit_Status = EditStatusType.Add;
    }

    // sets edit status to available
    setItemAvailable(item: any) {
        item.Edit_Status = EditStatusType.Available;
    }

    // sets edit status to add or change based on existing edit status
    setItemChanged(item: any, existingStatus: string) {
        switch (existingStatus) {
            case EditStatusType.NoChange:
                item.Edit_Status = EditStatusType.Change;
                break;

            case EditStatusType.Change: //Already "C", no need to change.
            case EditStatusType.Delete: //Don't allow changing Deleted records. 
            // Should be disabled in the Form. Should changing a child record set a Deleted record back to "Changed"?
            case EditStatusType.PreviousDelete: //Don't allow changing Previously Deleted records. You have to reactivate first.
            case EditStatusType.Reactivated: //Need to retain Reactivated status so we can change these to "A" before processing. 
                // SDK uses Edit_Status = "A" w/ a non-negative ID to reactivate.
                //Make no change to Edit_Status
                break;

            default:
                //No Edit_Status or existingStatus is "A" (Add) or "?"" (Available)
                item.Edit_Status = EditStatusType.Add;
                break;
        }
    }

    // sets edit status to deleted or deletes item if it is a new item
    setItemDeleted(items: any, id: any) {
        if (
            items[id].Edit_Status &&
            items[id].Edit_Status != EditStatusType.Add &&
            items[id].Edit_Status != EditStatusType.Available
        ) {
            if (items[id].Edit_Status == EditStatusType.Reactivated) {
                items[id] = { ...items[id], Edit_Status: EditStatusType.PreviousDelete };
            } else {
                items[id] = { ...items[id], Edit_Status: EditStatusType.Delete };
            }
        } else {
            delete items[id];
        }
    }

    // sets edit status to changed if item is currently marked deleted
    setItemRestored(items: any, id: any) {
        if (items[id].Edit_Status && items[id].Edit_Status == EditStatusType.Delete) {
            items[id] = { ...items[id], Edit_Status: EditStatusType.Change };
        } else if (items[id].Edit_Status && items[id].Edit_Status == EditStatusType.PreviousDelete) {
            items[id] = { ...items[id], Edit_Status: EditStatusType.Reactivated };
        }
    }

    // builds a new object based on defaults in an object default dictionary.  Can optional merge in local settings/defaults for the object
    createDefaultItem(defaultDictionary: any, itemType: string, localItem?: any) {
        let obj = cloneDeep(defaultDictionary[itemType]) || {};
        if (localItem) {
            obj = { ...obj, ...cloneDeep(localItem) };
        }
        return obj;
    }

    removeArrayItemByValue(arr: any[], value: any): any[] {
        let arrCopy = [...arr];
        for (var i = 0; i < arrCopy.length; i++) {
            if (arrCopy[i] === value) {
                arrCopy.splice(i, 1);
            }
        }
        return arrCopy;
    }

    addArrayItem(arr: any[], value: any): any[] {
        if (!arr) {
            arr = [];
        }
        arr.push(value);
        return arr;
    }

    setTaskControlAttributes(tranNode: any, currentInfo: any) {
        //This method is used by PrepareRequest state actions to set the task and project attributes on the transaction

        //Keep_Task_Open already set based on Terminating events and Mandatory WF events.

        //always keep project open
        tranNode.Keep_Project_Open = 'Y';

        //link the process transaction to the current task id (if there is a current task)
        if (currentInfo.Task_ID) {
            tranNode.Task_ID = currentInfo.Task_ID;
        }

        //link the process transaction to the current session/project (if there is a current project)
        if (currentInfo.Project_ID) {
            tranNode.Session_ID = currentInfo.Project_ID;
            //TODO: Should we consider change to SDK to use Project_ID?   Pass both Project_ID and Session_ID for compatability?
        }
    }

    propertyChanged(currentRecord: any, updatedRecord: any, propertyName: string): boolean {
        if (
            currentRecord[propertyName] != updatedRecord[propertyName] &&
            !(currentRecord[propertyName] === undefined && updatedRecord[propertyName] == '')
        ) {
            return true;
        } else {
            return false;
        }
    }

    stateValueChanged(updateValue: any, currentValue: any, handleInitialValueAsChanged: boolean = true): boolean {
        // !!Changes to this method must go through architectural review!!
        let valueChanged = false;

        if (updateValue || updateValue === '') {
            if (currentValue === undefined || currentValue === null) {
                //Treat undefined or null current value as empty string.
                currentValue = '';
            }

            valueChanged = updateValue != currentValue;

            if (valueChanged && currentValue == '') {
                valueChanged = handleInitialValueAsChanged;
            }
        }

        return valueChanged;
    }
}
